import { siteCalendarDay } from '@bms/common/utils';
/**
 * The desk's Activity feed as pure functions (plan-frontDeskUx T7; audit P4).
 *
 * Every row is one `events` log entry (BMS-spec `edge/data-model.md` §events,
 * "Desk activity events"): what kind of thing it is (the filter it belongs
 * to), the room it is about, a translated sentence, and who did it — the
 * actor column P4 draws as device / lock / contact / person / Edge / broker.
 * Nothing here prints a server string as the sentence; names, codes and
 * guest names are data and pass through.
 */
import type { EventLogEntry } from '@bms/common';

export const ACTIVITY_FILTERS = ['all', 'doors', 'keys', 'alarms', 'housekeeping', 'stays'] as const;
export type ActivityFilter = typeof ACTIVITY_FILTERS[number];
export type ActivityKind = Exclude<ActivityFilter, 'all'>;

/**
 * The `events.event_type` values each filter shows; `all` is their union less
 * `NOT_IN_ALL`.
 */
export const ACTIVITY_EVENT_TYPES: Record<ActivityKind, readonly string[]> = {
  doors: ['access_granted', 'access_denied', 'lock_left_open', 'access_control'],
  keys: [
    'card_issued', 'card_revoked', 'card_suspended', 'card_reactivated',
    'card_replaced', 'card_retired', 'card_acl_updated',
  ],
  alarms: ['alarm_raised', 'alarm_acknowledged', 'alarm_resolved', 'gateway_offline', 'gateway_online'],
  housekeeping: [
    'cleaning_started', 'cleaning_done', 'inspected', 'mur_on', 'mur_off', 'mur_acknowledged',
    'dnd_on', 'dnd_off', 'maintenance',
  ],
  stays: ['check_in', 'check_out', 'stay_updated', 'room_move', 'note', 'status_change'],
};

const KIND_OF_TYPE = new Map<string, ActivityKind>(
  (Object.entries(ACTIVITY_EVENT_TYPES) as Array<[ActivityKind, readonly string[]]>)
    .flatMap(([kind, types]) => types.map((type) => [type, kind] as const)),
);

/**
 * Gateway chatter stays under Alarms and in the Attention queue's "not
 * responding" row, but is not the desk's story (F16, A7: for every role). The
 * unread count reads `all`, so it drops them too.
 */
const NOT_IN_ALL = new Set(['gateway_offline', 'gateway_online']);

/** The `type` query parameter for a filter: a comma-separated list of event types. */
export function activityTypesParam(filter: ActivityFilter): string {
  const types = filter === 'all'
    ? Object.values(ACTIVITY_EVENT_TYPES).flat().filter((type) => !NOT_IN_ALL.has(type))
    : ACTIVITY_EVENT_TYPES[filter];
  return types.join(',');
}

export function activityKind(entry: Pick<EventLogEntry, 'eventType'>): ActivityKind | null {
  return KIND_OF_TYPE.get(entry.eventType) ?? null;
}

/** Device-originated alarm types (GRMS §9.1); every other alarm is Edge's own judgement. */
const DEVICE_ALARMS = new Set(['sos', 'lock_forced', 'tamper', 'burglary']);

export type ActivityActor =
  | { kind: 'person'; label: string }
  | { kind: 'device' | 'lock' | 'contact' | 'edge' | 'broker' | 'pms' };

/** The actor column: who or what caused the row (P4). */
export function activityActor(entry: EventLogEntry): ActivityActor {
  const actor = entry.actor ?? {};
  const actorType = entry.actorType ?? actor.type ?? 'system';
  if (actorType === 'user' || actorType === 'federated_user') {
    const label = (typeof actor.label === 'string' && actor.label) || (typeof actor.id === 'string' && actor.id) || '';
    if (label) return { kind: 'person', label };
  }
  switch (entry.eventType) {
    case 'access_granted':
    case 'access_denied':
      return { kind: 'lock' };
    case 'lock_left_open':
      return { kind: 'contact' };
    case 'gateway_offline':
    case 'gateway_online':
      return { kind: 'broker' };
    case 'dnd_on':
    case 'dnd_off':
    case 'mur_on':
    case 'mur_off':
      return { kind: 'device' };
    case 'alarm_raised':
      return DEVICE_ALARMS.has(String(entry.data?.alarmType)) ? { kind: 'device' } : { kind: 'edge' };
    default:
      return actor.id === 'pms' ? { kind: 'pms' } : { kind: 'edge' };
  }
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/** The room (or, for a gateway row, the gateway) the row is about; `null` when none. */
export function activityRoom(entry: EventLogEntry): string | null {
  const data = entry.data ?? {};
  if (entry.eventType === 'gateway_offline' || entry.eventType === 'gateway_online') {
    return str(entry.sourceId);
  }
  if (entry.eventType === 'room_move') return str(data.toRoomCode) ?? str(data.fromRoomCode);
  return str(data.roomCode)
    ?? str(entry.actor?.roomCode)
    ?? (entry.sourceType === 'room' ? str(entry.sourceId) : null);
}

export type ActivityTone = 'critical' | 'ok' | 'housekeeping' | 'info' | 'neutral';

/** A translation key and its values: the row's sentence, translated by the caller. */
export interface ActivitySentence {
  key: string;
  values: Record<string, string | number>;
}

export interface ActivityRow {
  id: string;
  at: Date;
  kind: ActivityKind;
  tone: ActivityTone;
  room: string | null;
  sentence: ActivitySentence;
  actor: ActivityActor;
}


function toneOf(entry: EventLogEntry): ActivityTone {
  switch (entry.eventType) {
    case 'access_denied':
    case 'gateway_offline':
      return 'critical';
    case 'alarm_raised':
      return entry.data?.severity === 'critical' ? 'critical' : 'info';
    case 'alarm_resolved':
    case 'inspected':
    case 'cleaning_done':
    case 'gateway_online':
    case 'access_granted':
      return 'ok';
    case 'mur_on':
    case 'cleaning_started':
    case 'mur_acknowledged':
      return 'housekeeping';
    case 'lock_left_open':
      return 'info';
    default:
      return 'neutral';
  }
}

/** The sentence's translation key (`activity.event.<…>`) and values for one row. */
export function activitySentence(entry: EventLogEntry): ActivitySentence {
  const data = entry.data ?? {};
  const type = entry.eventType;
  // Rows written before BMS-spec 0.110, or at a reader no lock is bound to,
  // carry only the gateway's own keys (`lock_code`, `card_uid`, `reason`);
  // such a row names the reader instead of a lock.
  const lock = str(data.lockName) ?? str(data.lockCode) ?? str(data.lock_code) ?? '';
  const holder = str(data.holderName) ?? str(entry.actor?.label) ?? '';
  // The whole UID (owner, 2026-10-22): a batch of cards can share its last six digits.
  const card = str(data.cardUid) ?? str(data.card_uid) ?? '';
  switch (type) {
    case 'access_granted':
    case 'access_denied': {
      const reason = str(data.denialReason) ?? str(data.reason) ?? '';
      if (!lock) return { key: `${type}_reader`, values: { reader: str(entry.sourceId) ?? '', card, reason } };
      if (type === 'access_denied') return { key: type, values: { lock, card, reason } };
      return { key: holder ? 'access_granted_holder' : 'access_granted', values: { lock, holder, card } };
    }
    case 'access_control': {
      const action = str(data.action) ?? '';
      const known = ['lock', 'unlock', 'open', 'emergency_unlock'].includes(action);
      return { key: known ? `access_control.${action}` : 'access_control.other', values: { lock } };
    }
    case 'alarm_raised':
    case 'alarm_acknowledged':
      return { key: type, values: { alarm: str(data.alarmType) ?? '' } };
    case 'alarm_resolved':
      return { key: data.automatic === true ? 'alarm_resolved_automatic' : 'alarm_resolved', values: { alarm: str(data.alarmType) ?? '' } };
    case 'gateway_offline':
    case 'gateway_online':
      return { key: type, values: { gateway: str(data.gatewayName) ?? str(entry.sourceId) ?? '' } };
    case 'check_in':
      return { key: str(data.guestName) ? 'check_in_guest' : 'check_in', values: { guest: str(data.guestName) ?? '' } };
    case 'room_move':
      return { key: 'room_move', values: { from: str(data.fromRoomCode) ?? '', to: str(data.toRoomCode) ?? '' } };
    case 'note':
      return { key: 'note', values: { note: str(data.note) ?? '' } };
    case 'card_issued':
    case 'card_revoked':
      return { key: holder ? `${type}_holder` : type, values: { holder, card, reason: str(data.reason) ?? '' } };
    default:
      return { key: type, values: { holder, card, lock } };
  }
}

/** One feed row from one event, or `null` for an event the feed does not show. */
export function activityRow(entry: EventLogEntry): ActivityRow | null {
  const kind = activityKind(entry);
  if (!kind) return null;
  return {
    id: entry.id,
    at: new Date(entry.createdAt),
    kind,
    tone: toneOf(entry),
    room: activityRoom(entry),
    sentence: activitySentence(entry),
    actor: activityActor(entry),
  };
}

export type ActivityDay =
  | { kind: 'today' }
  | { kind: 'yesterday' }
  | { kind: 'date'; date: Date };

export interface ActivityDayGroup {
  key: string;
  day: ActivityDay;
  rows: ActivityRow[];
}

/**
 * Rows split under day separators (site-local calendar days), newest day
 * first; the rows keep the order they came in (newest first from the server).
 */
export function groupActivityByDay(rows: readonly ActivityRow[], now: Date, timeZone?: string): ActivityDayGroup[] {
  const today = siteCalendarDay(now, timeZone);
  const yesterday = today - 1;
  const groups: ActivityDayGroup[] = [];
  for (const row of rows) {
    const dayNumber = siteCalendarDay(row.at, timeZone);
    const key = String(dayNumber);
    let group = groups[groups.length - 1];
    if (!group || group.key !== key) {
      const day: ActivityDay = dayNumber === today
        ? { kind: 'today' }
        : dayNumber === yesterday
          ? { kind: 'yesterday' }
          : { kind: 'date', date: row.at };
      group = { key, day, rows: [] };
      groups.push(group);
    }
    group.rows.push(row);
  }
  return groups;
}
